import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Database, Plug, Check, ExternalLink, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { useAuth } from "@/contexts/AuthContext";
import { connectionsApi } from "@/lib/api/connections";
import { getActiveWorkspaceId } from "@/stores/workspace-store";
import toast from "react-hot-toast";
import { Connection, ConnectionStatus } from "@optidev/shared-types";
import { cn } from "@/lib/utils";

interface ProjectConnectionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  existingConnectionIds?: string[];
}

const getConnectorIcon = (connectorId: string | undefined) => {
  switch (connectorId) {
    case "mongodb":
      return <Database className="h-4 w-4 text-green-500" />;
    case "bigquery":
    case "google-bigquery":
      return <Database className="h-4 w-4 text-blue-500" />;
    case "postgresql":
      return <Database className="h-4 w-4 text-indigo-500" />;
    case "mysql":
      return <Database className="h-4 w-4 text-orange-500" />;
    default:
      return <Database className="h-4 w-4 text-muted-foreground" />;
  }
};

const getStatusDot = (status: ConnectionStatus) => {
  switch (status) {
    case ConnectionStatus.ACTIVE:
      return <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />;
    case ConnectionStatus.ERROR:
      return <div className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" />;
    case ConnectionStatus.PENDING:
      return <div className="w-2 h-2 rounded-full bg-amber-500 flex-shrink-0" />;
    default:
      return <div className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0" />;
  }
};

const formatTypeName = (type: string | undefined): string => {
  if (!type) return "Unknown";
  return type
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function ProjectConnectionsModal({
  isOpen,
  onClose,
  projectId,
  existingConnectionIds = [],
}: ProjectConnectionsModalProps) {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(existingConnectionIds),
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { getIdToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(existingConnectionIds));
      loadConnections();
    }
  }, [isOpen]);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      const data = await connectionsApi.getAll(token);
      setConnections(data);
    } catch (err) {
      console.error("Failed to load connections:", err);
      toast.error("Failed to load connections");
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (connectionId: string) => {
    const next = new Set(selectedIds);
    if (next.has(connectionId)) {
      next.delete(connectionId);
    } else {
      next.add(connectionId);
    }
    setSelectedIds(next);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error("Authentication required");

      const workspaceId = getActiveWorkspaceId();
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/editor/projects/${projectId}/connections`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            ...(workspaceId && { "X-Workspace-Id": workspaceId }),
          },
          body: JSON.stringify({
            connectionIds: Array.from(selectedIds),
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to update project connections");

      toast.success("Connections updated");
      onClose();
    } catch (err) {
      console.error("Failed to update connections:", err);
      toast.error("Failed to update connections");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNew = () => {
    onClose();
    navigate("/dashboard/connectors?tab=connectors");
  };

  const hasChanges =
    selectedIds.size !== existingConnectionIds.length ||
    existingConnectionIds.some((id) => !selectedIds.has(id));

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5 text-muted-foreground" />
            Add Connections
          </DialogTitle>
          <DialogDescription>
            Select existing connections to link to this project.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[360px] overflow-y-auto -mx-1 px-1">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : connections.length === 0 ? (
            <div className="text-center py-10">
              <Plug className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-1">
                No connections found
              </p>
              <p className="text-xs text-muted-foreground">
                Create a connection first from the Connectors page.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {connections.map((connection) => {
                const isSelected = selectedIds.has(connection.id);
                return (
                  <button
                    key={connection.id}
                    onClick={() => handleToggle(connection.id)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors",
                      isSelected
                        ? "border-primary/50 bg-primary/5"
                        : "border-border/60 hover:bg-muted/50",
                    )}
                  >
                    <div
                      className={cn(
                        "h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
                        isSelected
                          ? "border-primary bg-primary"
                          : "border-muted-foreground/30",
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    {getConnectorIcon(connection.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium truncate">
                          {connection.name}
                        </span>
                        {getStatusDot(connection.status)}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {formatTypeName(connection.type)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCreateNew}
            className="sm:mr-auto text-muted-foreground"
          >
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
            Create New Connection
          </Button>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading || !hasChanges}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
